import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Constants from 'src/app/core/constants/constants';
import { ParticipationMode, ParticipantStatus, SocialPlatform } from 'src/app/core/models/entities/EntityEnum';
import { SubscriptionModel } from 'src/app/core/models/entities/subscription.model';
import { UserModel } from 'src/app/core/models/entities/user.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { SubscriptionService } from 'src/app/core/services/contenful/subscription.service';
import { MailService } from 'src/app/core/services/mail.service';
import { FacebookUrlValidator } from 'src/app/core/validators/facebookUrlValidator';
import { groupParticipantsValidator } from 'src/app/core/validators/group-participants.validator';
import { InstagramUrlValidator } from 'src/app/core/validators/instagramUrlValidator';
import { socialPlatformRequiredValidator } from 'src/app/core/validators/socialPlatformRequiredValidator';

@Component({
    selector: "app-signup",
    templateUrl: "./signup.component.html",
    styleUrls: ["./signup.component.scss"],
})
export class SignupComponent implements OnInit {
    form!: FormGroup;

    ParticipationMode = ParticipationMode;
    ParticipantStatus = ParticipantStatus;
    SocialPlatform = SocialPlatform;
    selectedParticipant?: UserModel;
    showParticipantModal = false;
    isChiefAdding: boolean = false;
    participationMode: ParticipationMode = ParticipationMode.INDIVIDUAL;
    submitted = false;
    invalidFields: string[] = [];

    constructor(
        private router: Router,
        private fb: FormBuilder,
        private titleService: Title,
        private subscriptionService: SubscriptionService,
        private authService: AuthService,
        private mailService: MailService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService
    ) {
        this.titleService.setTitle("DASP - Inscription");
    }

    ngOnInit(): void {
        this.form = this.fb.group(
            {
                participationMode: [
                    ParticipationMode.INDIVIDUAL,
                    Validators.required,
                ],

                groupChief: [null, Validators.required],
                groupParticipant: [[]],

                isSubscribedToDasp: [false],
                socialPlatform: [[]],
                facebookAccount: [""],
                facebookLink: ["", FacebookUrlValidator],
                instagramAccount: [""],
                instagramLink: ["", InstagramUrlValidator],

                exactInformations: [false, Validators.requiredTrue],
                hasAcceptedTerms: [false, Validators.requiredTrue],
                canUseContentForMarketing: [false],

                confirmedRegistration: [false, Validators.requiredTrue],
            },
            {
                validators: [
                    groupParticipantsValidator,
                    socialPlatformRequiredValidator,
                ],
            }
        );

        this.listenSubscriptionChange();
    }

    private listenSubscriptionChange() {
        this.form
            .get("isSubscribedToDasp")!
            .valueChanges.subscribe((isSubscribed) => {
                if (!isSubscribed) {
                    this.clearSocialValidators();
                    return;
                }

                this.applySocialValidators();
            });
    }

    private applySocialValidators(platform: SocialPlatform = null) {
        if (platform == SocialPlatform.FACEBOOK) {
            this.setRequired("facebookAccount");
            this.setRequired("facebookLink");
            this.clearSocialInstagramValidators();
        }

        if (platform == SocialPlatform.INSTAGRAM) {
            this.setRequired("instagramAccount");
            this.setRequired("instagramLink");
            this.clearSocialFacebookValidators();
        }
    }

    private clearSocialValidators() {
        this.clearSocialFacebookValidators();
        this.clearSocialInstagramValidators();
    }

    private clearSocialFacebookValidators() {
        ["facebookAccount", "facebookLink"].forEach((control) => {
            this.form.get(control)?.clearValidators();
            this.form.get(control)?.updateValueAndValidity();
        });
    }

    private clearSocialInstagramValidators() {
        ["instagramAccount", "instagramLink"].forEach((control) => {
            this.form.get(control)?.clearValidators();
            this.form.get(control)?.updateValueAndValidity();
        });
    }

    private setRequired(controlName: string) {
        this.form.get(controlName)?.setValidators([Validators.required]);
        this.form.get(controlName)?.updateValueAndValidity();
    }

    backToLogin() {
        this.authService.logout();
        this.router.navigate(["/login"]);
    }

    isInvalid(controlName: string): boolean {
        const control = this.form.get(controlName);
        return !!(
            control &&
            control.invalid &&
            (control.dirty || control.touched)
        );
    }

    // -------- SOCIAL --------
    toggleSocialPlatform(platform: SocialPlatform): void {
        const platforms = this.form.value.socialPlatform as SocialPlatform[];

        if (platforms.includes(platform)) {
            this.form.patchValue({
                socialPlatform: platforms.filter((p) => p !== platform),
            });
        } else {
            this.form.patchValue({
                socialPlatform: [...platforms, platform],
            });
        }

        this.applySocialValidators(platform);
    }

    get isfacebookSelected(): boolean {
        const platforms = this.form.value.socialPlatform as SocialPlatform[];
        return platforms.includes(SocialPlatform.FACEBOOK);
    }

    get isInstagramSelected(): boolean {
        const platforms = this.form.value.socialPlatform as SocialPlatform[];
        return platforms.includes(SocialPlatform.INSTAGRAM);
    }

    // -------- PARTICIPANTS --------
    get groupParticipants(): FormArray {
        return this.form.get("groupParticipant") as FormArray;
    }

    openAddParticipant(isChief: boolean = false): void {
        this.selectedParticipant = undefined;
        this.showParticipantModal = true;
        this.participationMode = this.form.get("participationMode")?.value;
        this.isChiefAdding = isChief;
    }

    openEditParticipant(p: UserModel, isChief: boolean = false): void {
        this.selectedParticipant = p;
        this.showParticipantModal = true;
        this.participationMode = this.form.get("participationMode")?.value;
        this.isChiefAdding = isChief;
    }

    saveParticipant(p: UserModel): void {
        if (this.isChiefAdding) {
            this.saveChiefParticipant(p);
        } else {
            this.saveGroupParticipant(p);
        }
    }

    saveGroupParticipant(p: UserModel): void {
        this.showParticipantModal = false;
        const participants: UserModel[] =
            this.form.get("groupParticipant")?.value || [];

        const index = participants.findIndex((x) => x.id === p.id);

        if (index >= 0) {
            participants[index] = p;
        } else {
            participants.push(p);
        }

        if (this.form.get("groupParticipant")?.value.length < 3) {
            this.form.patchValue({ groupParticipant: [...participants] });
        }

        this.showParticipantModal = false;
    }

    saveChiefParticipant(p: UserModel): void {
        this.form.patchValue({ groupChief: p });
        this.showParticipantModal = false;
    }

    removeParticipant(id: string, isChief: boolean = false): void {
        if (isChief) {
            this.form.patchValue({ groupChief: null });
            this.form.get("groupChief")?.reset();
        } else {
            const participants = this.form.get("groupParticipant")?.value || [];
            const value = participants.filter((p) => p.id !== id);
            this.form.patchValue({
                groupParticipant: value,
            });
            this.form.get("groupParticipant")?.setValue([...value]);
        }
    }

    // -------- SUBMIT --------
    async submit(): Promise<void> {
        this.submitted = true;
        this.invalidFields = [];

        if (this.form.invalid) {
            this.markAllAsTouched(this.form);
            this.collectInvalidFields(this.form);

            this.scrollToFirstError();
            this.toastr.error(
                "Erreur de validation du formulaire. Bien vouloir les corriger et réessayer.",
                Constants.ToastrTitle
            );
            return;
        }

        const payload: SubscriptionModel = {
            ...this.form.value,
        };

        try {
            this.spinner.show();
            const result = await this.subscriptionService.create(payload);

            await this.sendConfirmationEmail(payload);
        } catch (error) {
            console.error("Erreur lors de l'inscription :", error);

            this.toastr.error(
                "Une erreur est survenue lors de l'inscription. Veuillez réessayer.",
                Constants.ToastrTitle
            );
        } finally {
            this.spinner.hide();
        }
    }

    private markAllAsTouched(formGroup: FormGroup | FormArray) {
        Object.values(formGroup.controls).forEach((control) => {
            if (control instanceof FormGroup || control instanceof FormArray) {
                this.markAllAsTouched(control);
            } else {
                control.markAsTouched();
            }
        });
    }

    private collectInvalidFields(
        formGroup: FormGroup | FormArray,
        parentKey: string = ""
    ) {
        const isIndividualMode = this.form.get("participationMode").value === ParticipationMode.INDIVIDUAL;
        if (this.form.errors?.["minGroupParticipants"]) {
            this.invalidFields.push("groupParticipant");
        }
        Object.keys(formGroup.controls).forEach((key) => {
            const control = formGroup.get(key);
            let fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (control instanceof FormGroup || control instanceof FormArray) {
                this.collectInvalidFields(control, fullKey);
            } else {
                if (control && control.invalid) {
                    if (fullKey === "groupChief" && isIndividualMode){
                        fullKey = "aloneParticipant";
                    }

                    this.invalidFields.push(fullKey);
                }
            }
        });
        if (this.form.errors?.["subscribeTosocialRequired"]) {
            this.invalidFields.push("subscribeTosocialRequired");
        }
        if (this.form.errors?.["socialRequired"]) {
            this.invalidFields.push("socialAccout");
        }
    }

    scrollToFirstError(): void {
        setTimeout(() => {
            const firstInvalidControl: HTMLElement | null =
                document.querySelector(".is-invalid, .ng-invalid.ng-touched");

            if (firstInvalidControl) {
                firstInvalidControl.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                // focus si possible
                if ("focus" in firstInvalidControl) {
                    (firstInvalidControl as HTMLElement).focus();
                }
            }
        });
    }

    getFieldLabel(field: string): string {
        return FieldForm.fieldLabels[field] || field;
    }

    async sendConfirmationEmail(
        subscription: SubscriptionModel
    ): Promise<void> {
        try {
            const templateParams = {
                to_email: subscription.groupChief?.email,
                fullName: subscription.groupChief.fullName,
                // participationMode: subscription.participationMode.toString(),
                // emailChief: subscription.groupChief?.email,
                // emailParticipants: subscription.groupParticipant
                //     ?.map((p) => p.email)
                //     .join(", "),
                subscriptionId: subscription.id,
            };
            await this.mailService.sendMail(
                templateParams,
                Constants.ConfirmationTemplateIdKey
            );
        } catch (error) {
            console.error("Erreur email", error);
        } finally {
            this.toastr.success("Inscription réussie !", Constants.ToastrTitle);
            const newUser = this.getUserToLogin(subscription);
            if (!newUser) {
                this.authService.logout();
                this.router.navigate(["/login"]);
                return;
            }

            this.authService.login(newUser);
            this.router.navigate(["/"]);
        }
    }

    getUserToLogin(subscription: SubscriptionModel): UserModel | null {
        const userConnected = this.authService.getUserConnected();
        let newUser: UserModel;
        if (userConnected.email == subscription.groupChief?.email) {
            newUser = { ...subscription.groupChief } as UserModel;
        } else {
            newUser = subscription.groupParticipant?.find(
                (p) => p.email == userConnected.email
            ) as UserModel;
        }

        return newUser;
    }
}

export class FieldForm {
    static readonly fieldLabels: Record<string, string> = {
        participationMode: "Mode de participation",
        groupChief: "Chef de groupe",
        aloneParticipant: "Participant",
        groupParticipant: "Participants du groupe (max 3)",

        subscribeTosocialRequired:
            "Vous devez etre abonné à au moins une page de DASP (Facebook ou Instagram).",
        socialAccout: "Au moins un réseau social doit être renseigné.",
        facebookAccount: "Nom du compte Facebook",
        facebookLink: "Lien du profil Facebook",
        instagramAccount: "Nom du compte Instagram",
        instagramLink: "Lien du profil Instagram",

        exactInformations: "Exactitude des informations",
        hasAcceptedTerms: "Acceptation des conditions",
        confirmedRegistration: "Confirmation de l’inscription",
    };
}
